import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { useTranslation } from "react-i18next";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { useSnackbar } from "notistack";
import {
  updateCompensations,
  updateSanctions,
  catalogByPart,
} from "../../../../../../../apis";
import EGDatePicker from "../../../../../../MainComponents/DatePicker/EGDatePicker";
import dayjs from "dayjs";
import { useReportId } from "../../../../../../../context/report/ReportContext";

const SANCTIONS_CATALOGUE = "RS-102103";

export const EditSanctionsCompensationsForm = ({
  content,
  hdlCloseDialog,
  reloadData,
  reportRender,
  type,
}) => {
  // Custom Hooks
  const id = useReportId();
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { involvedType, sanctionsOptions, benefitOptions } = useReportContext();
  const lang = localStorage.getItem("lang") || "es";
  const isSanctions = type === "sanctions";
  // Initial States
  const [isLoading, setIsLoading] = useState(false);
  const [selectedActions, setSelectedActions] = useState(
    content.sanctions || []
  );
  const [addComment, setAddComment] = useState(content.comment || false);
  const [comment, setComment] = useState(content.comment || "");
  const [another, setAnother] = useState(content.another || []);
  const [determinationDate, setDeterminationDate] = useState(
    content.determination_date ? dayjs(content.determination_date) : null
    );
  const [executionDate, setExecutionDate] = useState(
    content.execution_date ? dayjs(content.execution_date) : null
  );
  // Catalog state for sanctions
  const [catalogSanctionsOpts, setCatalogSanctionsOpts] = useState(null);
  const useCatalog = isSanctions && Array.isArray(catalogSanctionsOpts);

  // Consultar catálogo de sanciones
  useEffect(() => {
    if (!isSanctions) return;
    const fetchCatalog = async () => {
      try {
        const resp = await catalogByPart({ is_own: false, catalogue: SANCTIONS_CATALOGUE, path: "/" });
        if (!resp.error && Array.isArray(resp.data) && resp.data.length > 0) {
          const capitalize = (str) => str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
          setCatalogSanctionsOpts(
            resp.data.map((item) => ({
              label: capitalize(item.locales?.[lang] || item.label),
              value: item.path_locales,
              locales: item.locales || {},
            }))
          );
        }
      } catch {
        // Si falla, se usan las opciones hardcodeadas
      }
    };
    fetchCatalog();
  }, []);

  useEffect(() => {
    setDeterminationDate(
      content.determination_date ? dayjs(content.determination_date) : null
    );
    setExecutionDate(
      content.execution_date ? dayjs(content.execution_date) : null
    );
  }, [content]);
  // Dinamic titles & colors
  const titlesType = {
    sanctions: {
      title: t("Resolution.edit_sanctions"),
      inputLabel: t("Resolution.sanctions"),
      inputPlaceholder: t("Resolution.select_one_more_sanctions"),
      addAnotherAction: t("Resolution.add_another_sanction"),
      addActionInput: t("Resolution.another_sanction"),
      color: "orange",
      colorHover: "orangeII",
      commentPlaceholder: t(
        "Resolution.provide_details_sanction_assigned_person_involved"
      ),
    },
    benefits: {
      title: t("Resolution.edit_compensations"),
      inputLabel: t("Resolution.benefits"),
      inputPlaceholder: t("Resolution.select_one_more_benefits"),
      addActionInput: t("Resolution.add_another_compensation"),
      addActionInput: t("Resolution.another_compensation"),
      color: "vividViolet",
      colorHover: "vividVioletII",
      commentPlaceholder: t(
        "Resolution.provide_details_compensations_awarded_person_involved"
      ),
    },
  };
  const property =
    type === "sanctions" ? "another_sanction" : "another_benefit";
  // Dynamic Options (usa catálogo si existe para sanciones)
  const OPTIONS_MAP = {
    sanctions: useCatalog ? catalogSanctionsOpts : sanctionsOptions,
    benefits: benefitOptions,
  };
  const options = OPTIONS_MAP[type] || [];

  // Helper para obtener el value de una acción (string o objeto)
  const getActionValue = (action) =>
    typeof action === "object" && action?.value ? action.value : action;

  // Sets the value in an array []
  const hdlActionsChange = (_, newValue) => {
    if (useCatalog) {
      setSelectedActions(newValue.map((option) => ({
        value: option.value,
        label: option.label,
        locales: option.locales || {},
      })));
    } else {
      setSelectedActions(newValue.map((option) => option.value));
    }
  };

  // Add another sanction
  const hdlAddAnotherAction = () => {
    setAnother((prev) => [...prev, { [property]: "" }]);
  };

  // Add another action
  const hdlInputAnotherActionChange = (index, value) => {
    setAnother((prev) =>
      prev.map((item, i) => (i === index ? { item, [property]: value } : item))
    );
  };

  // Delete another action
  const hdlDeleteAnotherAction = (index) => {
    setAnother(another.filter((_, i) => i !== index));
  };

  // Validate data
  const hasEmptyAnother = () => {
    another.some((item) => item[property]?.trim() === "");
  };

  const isInvalid =
    (!selectedActions.length && !another.length) || hasEmptyAnother();

  const isAnotherOptionInvalid = hasEmptyAnother();

  // Send info
  const onSubmitActions = async () => {
    setIsLoading(true);
    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    const API_CALLS = {
      sanctions: updateSanctions,
      benefits: updateCompensations,
    };

    const endpoint = API_CALLS[type] || null;

    const dataToSend = {
      id: content.id,
      report_identifier: id,
      ...(selectedActions.length > 0 && { [type]: selectedActions }),
      ...(another.length > 0 && { another }),
      comment,
      determination_date: determinationDate ? determinationDate?.format("YYYY-MM-DD") : null,
      execution_date: executionDate ? executionDate?.format("YYYY-MM-DD") : null,
    };

    if (!endpoint) {
      console.error("No API function available for type:", type);
      closeSnackbar(sending);
      setIsLoading(false);
      return;
    }

    try {
      await endpoint(dataToSend).then((resp) => {
        if (resp.status === 201 || 200) {
          setSelectedActions([]);
          setAddComment(false);
          setComment("");
          setAnother([]);
          setDeterminationDate(null);
          setExecutionDate(null);
          reloadData();
          reportRender();
          setIsLoading(false);
          closeSnackbar(sending);
          enqueueSnackbar(`${t("Resolution.data_successfully_updated")}`, {
            variant: "success",
          });
          setIsLoading(false);
          hdlCloseDialog();
        }
      });
    } catch (error) {
      console.error(error);
      closeSnackbar(sending);
      setIsLoading(false);
    }
  };

  return (
    <>
      <EGIconButton
        iconName="cancel_circle"
        iconColor="primary"
        onClick={hdlCloseDialog}
        sx={{ position: "absolute", top: 15, right: 12 }}
        iconSize={25}
      />
      <DialogTitle
        align="center"
        component="h3"
        color="secondary"
        sx={{ padding: 0 }}
      >
        {titlesType[type].title}
      </DialogTitle>
      <ListItem dense sx={{ padding: 0 }} className="border-bottom">
        <ListItemAvatar>
          <Avatar sx={{ backgroundColor: "transparent" }}>
            <EGIconButton
              iconName={
                content.confirm
                  ? "outline_profile_tick"
                  : "outline_profile_remove"
              }
              color={content.confirm ? titlesType[type].color : "blueGray500"}
              title={
                content.confirm
                  ? t("Resolution.confirmed")
                  : t("Resolution.unconfirmed")
              }
              colorHover={
                content.confirm ? titlesType[type].colorHover : "blueGray700"
              }
            />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              maxWidth={"100%"}
            >
              <Typography
                mr={0.5}
                color="secondary"
                fontWeight={600}
                variant="body1"
                component="span"
              >
                {`${involvedType[content.kind]}: `}
              </Typography>
              <Typography
                color={titlesType[type].color}
                variant="body1"
                component="span"
                fontWeight={700}
              >
                {content.name}
              </Typography>
            </Box>
          }
          secondary={
            <Stack
              spacing={2}
              display="flex"
              alignItems="center"
              justifyContent="normal"
              flexDirection="row"
              flexWrap="wrap"
              py={0.5}
              component={"span"}
            >
              <Typography variant="body2" component="span">
                {involvedType[content.kind || content.rol]}
              </Typography>
              {content.additional && (
                <Typography variant="body2" component="span">{`${t(
                  "Resolution.employee_number"
                )}: ${content.additional}`}</Typography>
              )}
              {content.relationship && (
                <Typography variant="body2" component="span">{`${t(
                  "Resolution.job_position"
                )}: ${content.relationship}`}</Typography>
              )}
              {content.details && (
                <Typography variant="body2" component="span">{`${t(
                  "Resolution.area"
                )}: ${content.details}`}</Typography>
              )}
            </Stack>
          }
        />
      </ListItem>

      <DialogContent sx={{ px: 0, py: 0.5 }}>
        {/* Preset action selection */}
        <Autocomplete
          multiple
          id={`assign-${type}`}
          options={options}
          disableCloseOnSelect
          getOptionLabel={(option) => option.label}
          sx={{ width: "100%", mx: "auto", mt: 1.5 }}
          value={options.filter((option) =>
            selectedActions.some((a) => getActionValue(a) === option.value)
          )}
          onChange={hdlActionsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                sx={{
                  backgroundColor: theme.palette.blueGray100.main,
                  fontSize: 12,
                }}
                {...getTagProps({ index })}
                label={
                  <Stack
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexDirection="row"
                    flexWrap="nowrap"
                  >
                    {option.label}
                  </Stack>
                }
              />
            ))
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={(props, option, { selected }) => {
            return (
              <Box
                {...props}
                key={option.value}
                sx={{
                  maxHeight: 47,
                  p: 2,
                  my: 0.5,
                  mx: 1,
                  borderRadius: 1.5,
                  border: `${theme.palette.blueGray100.main} 1px solid`,
                }}
              >
                <Checkbox size="small" checked={selected} />
                <Typography variant="body2">{option.label}</Typography>
              </Box>
            );
          }}
          renderInput={(params) => (
            <EGInput
              {...params}
              label={`${titlesType[type].inputLabel}:`}
              placeholder={titlesType[type].inputPlaceholder}
            />
          )}
        />

          <Box sx={{ mt: 1.5 }}>
            <EGDatePicker
              label={t("Resolution.determination_date")}
              value={determinationDate}
              onChange={(newValue) => setDeterminationDate(newValue)}
              >
            </EGDatePicker>
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <EGDatePicker
              label={t("Resolution.execution_date")}
              value={executionDate}
              onChange={(newValue) => setExecutionDate(newValue)}
            >
            </EGDatePicker>
            </Box>
        {/* Another sanctions/remunerations */}

        {another && (
          <Box maxHeight={"25vh"} overflow={"auto"} mt={0.75} py={0.5}>
            {another.map((action, i) => (
              <Stack
                key={i}
                display="flex"
                spacing={0.5}
                alignItems="start"
                flexDirection="row"
                flexWrap="nowrap"
                mt={1.33}
              >
                <EGInput
                  label={`${titlesType[type].addActionInput} #${i + 1}:`}
                  placeholder={titlesType[type].addOtherActionPlaceholder}
                  fullWidth
                  value={action[property]}
                  onChange={(e) =>
                    hdlInputAnotherActionChange(i, e.target.value)
                  }
                  required
                  autoFocus
                  helperText={titlesType[type].addOtherActionPlaceholder}
                />
                <EGIconButton
                  iconName="close_icon"
                  iconColor="error"
                  onClick={() => hdlDeleteAnotherAction(i)}
                  title={titlesType[type].deleteAction}
                />
              </Stack>
            ))}
          </Box>
        )}
        {/* Add another action button */}
        <Stack
          display="flex"
          alignItems="center"
          justifyContent="end"
          flexDirection="row"
          my={0.5}
          spacing={2}
        >
          <EGIconButton
            iconName="add_square"
            iconColor="primary"
            title={titlesType[type].addAnotherAction}
            onClick={hdlAddAnotherAction}
            disabled={isAnotherOptionInvalid}
          />
          {/* Add comment?  */}
          {(selectedActions.length > 0 || another.length > 0) &&
            !addComment && (
              <Link onClick={() => setAddComment(true)} underline="hover">
                + {t("Care.add_comment")}
              </Link>
            )}
        </Stack>
        {addComment && (
          <EGInput
            label={`${t("Resolution.details")}: ${comment.length}/500`}
            placeholder={titlesType[type].commentPlaceholder}
            sx={{ mt: 0.5 }}
            multiline
            rows={3}
            value={comment}
            autoFocus
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 1, mt: 0.5 }}>
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitActions}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCloseDialog}
          loading={isLoading}
        />
      </DialogActions>
    </>
  );
};
